import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { validateCash, type CashierDay } from "@/services/closingService";
import { PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import type { PaymentMethod } from "@/types";
import { formatMoney } from "@/utils/format";

const METHODS = Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[];

interface Props {
  cashier: CashierDay | null;
  date: string;
  onClose: () => void;
  onValidated: () => void;
}

/** Comptage d'une caisse : ce que le caissier a réellement en main, par mode ; l'écart est calculé en direct. */
export default function CountModal({ cashier, date, onClose, onValidated }: Props) {
  const { notify } = useToast();
  const [counted, setCounted] = useState<Partial<Record<PaymentMethod, string>>>({});
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCounted({});
    setComment("");
  }, [cashier]);

  if (!cashier) return null;

  // Modes à compter : ceux encaissés ce jour, plus les espèces (toujours présentes en caisse).
  const methods = METHODS.filter((m) => m === "ESPECES" || (cashier.expected[m] ?? 0) > 0);
  const countedTotal = methods.reduce((acc, m) => acc + (Number(counted[m]) || 0), 0);
  const gap = countedTotal - cashier.total;
  const allEntered = methods.every((m) => counted[m] !== undefined && counted[m] !== "");

  const submit = async () => {
    if (!allEntered) {
      notify("Saisissez le montant compté pour chaque mode (0 s'il n'y a rien).", "error");
      return;
    }
    if (gap !== 0 && !comment.trim()) {
      notify("Il y a un écart : expliquez-le dans le commentaire.", "error");
      return;
    }
    setSaving(true);
    try {
      await validateCash({
        date,
        cashierKey: cashier.cashierKey,
        counted: Object.fromEntries(methods.map((m) => [m, Number(counted[m]) || 0])),
        comment,
      });
      notify(`Caisse de ${cashier.cashierName} validée.`);
      onValidated();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Validation impossible.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Compter et valider la caisse"
      subtitle={`${cashier.cashierName} — ${date.split("-").reverse().join("/")}`}
      icon="ri-safe-2-line"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-checkbox-circle-line" onClick={submit} disabled={saving}>
            {saving ? "Validation..." : "Valider la caisse"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-foreground-600">
          Comptez ce que vous avez réellement en main pour chaque mode. Une fois validée, la journée de ce caissier
          est figée : plus aucun encaissement ni annulation ne peut y être fait.
        </p>
        <div className="overflow-hidden rounded-md border border-background-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
              <tr>
                <th className="px-3 py-2 font-medium">Mode</th>
                <th className="px-3 py-2 font-medium text-right">Attendu</th>
                <th className="px-3 py-2 font-medium">Compté</th>
              </tr>
            </thead>
            <tbody>
              {methods.map((m) => (
                <tr key={m} className="border-t border-background-200">
                  <td className="px-3 py-2 text-foreground-800">{PAYMENT_METHOD_LABEL[m]}</td>
                  <td className="px-3 py-2 text-right text-foreground-600">{formatMoney(cashier.expected[m] ?? 0)}</td>
                  <td className="px-3 py-1.5">
                    <Input
                      type="number"
                      min={0}
                      value={counted[m] ?? ""}
                      onChange={(e) => setCounted({ ...counted, [m]: e.target.value })}
                      placeholder="0"
                      aria-label={`Montant compté — ${PAYMENT_METHOD_LABEL[m]}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-background-200 bg-background-100/60 font-semibold">
              <tr>
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{formatMoney(cashier.total)}</td>
                <td className="px-3 py-2">{formatMoney(countedTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        {allEntered && (
          <p
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              gap === 0 ? "bg-primary-50 text-primary-800" : "bg-accent-50 text-accent-800"
            }`}
          >
            {gap === 0
              ? "La caisse est juste : aucun écart."
              : `Écart : ${gap > 0 ? "+" : ""}${formatMoney(gap)} (${gap < 0 ? "manquant" : "excédent"})`}
          </p>
        )}
        <Textarea
          label={gap !== 0 ? "Explication de l'écart (obligatoire)" : "Commentaire (facultatif)"}
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>
    </Modal>
  );
}
